import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { SEOMetaTags } from '../../components/shared/SEOMetaTags';
import { useToast } from '../../hooks/useToast';
import { useAuth } from '../../hooks/useAuth';
import { useTenantContext } from '../../context/TenantContext';
import { useDomainQuery, getDomainFilterParams } from '../../hooks/useDomainQuery';
import { getStudentProfileByToken, getStudentProfileByEmailAndDomain, invalidateStudentAccessToken } from '../../services/elearningStudentService';
import { 
  CreditCard, 
  Loader2, 
  BookOpen, 
  Users, 
  Clock, 
  Star, 
  CheckCircle,
  AlertCircle,
  ArrowLeft,
  Sparkles,
  Crown,
  Diamond,
  Gem,
  Wrench,
  Server,
  Code,
  HardDrive,
  Shield,
  Award,
  Zap,
  Cloud,
  LogOut,
  UserCheck,
  UserPlus
} from 'lucide-react';
import { PublicLayout } from '../../components/layout/PublicLayout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '../../components/ui/card';
import { Separator } from '../../components/ui/separator';
import { 
  useElearningCartStore, 
  useElearningCartStats, 
  useElearningCartActions,
  formatPrice,
  formatDifficultyLevel,
  formatCourseDuration
} from '../../stores/useElearningCartStore';
import { createElearningOrder } from '../../services/elearningOrderService';
import type { ElearningCustomerInfo } from '../../services/elearningOrderService';
import { ElearningPaymentModal } from '../../components/elearning/PaymentModal';
import CouponInput from '../../components/shared/CouponInput';
import { applyCoupon, type Coupon } from '../../services/couponService';

// Mapping d'icônes pour personnalisation
const iconMap: Record<string, React.ElementType> = {
  BookOpen,
  Sparkles,
  Star,
  Diamond,
  Gem,
  Crown,
  Wrench,
  Server,
  Code,
  HardDrive,
  Shield,
  Award,
  Zap,
  Cloud,
};

function hexToRgba(hex: string, alpha = 1) {
  const cleaned = hex.replace('#', '');
  const bigint = parseInt(cleaned.length === 3
    ? cleaned.split('').map((c) => c + c).join('')
    : cleaned, 16);
  const r = (bigint >> 16) & 255;
  const g = (bigint >> 8) & 255;
  const b = bigint & 255;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

const fadeIn = { 
  hidden: { opacity: 0, y: 20 }, 
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } } 
};

const ElearningCheckoutPage: React.FC = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { user } = useAuth();
  const { tenant, previewConfig } = useTenantContext();

  // Thème E-learning (preview > tenant)
  const elearningTheme = React.useMemo(() => {
    const base = (previewConfig?.theme?.notFound) ?? (tenant?.config?.theme?.notFound) ?? ({} as any);
    const themeColors = {
      c1: (tenant?.config?.color1 as string) || '#fe8d17', // primaire (bouton)
      c2: (tenant?.config?.color2 as string) || '#008aef', // secondaire (bouton)
      c3: (tenant?.config?.color3 as string) || '#ffffff',
      c4: (tenant?.config?.color4 as string) || '#9ca3af',
    };
    const defaults = {
      overlay: {
        enabled: true,
        useGradient: true,
        orientation: 'to-b', // to-b, to-t, to-r, to-l, to-tr, to-br, to-tl, to-bl
        color1: '#08062b',
        color2: '#08062b',
        color3: '#08062b',
        opacity1: 0.95,
        opacity2: 0.80,
        opacity3: 0.95,
      },
      icons: [
        { enabled: true, icon: 'BookOpen', color: themeColors.c1, size: 32 }, // top-left
        { enabled: true, icon: 'Sparkles',   color: themeColors.c2, size: 28 }, // top-right
        { enabled: true, icon: 'Diamond',    color: themeColors.c2, size: 24 }, // bottom-left
        { enabled: true, icon: 'Star',       color: themeColors.c1, size: 30 }, // bottom-right
      ]
    } as any;
    return {
      overlay: {
        enabled: base.overlay?.enabled ?? defaults.overlay.enabled,
        useGradient: base.overlay?.useGradient ?? defaults.overlay.useGradient,
        orientation: base.overlay?.orientation ?? defaults.overlay.orientation,
        color1: base.overlay?.color1 ?? defaults.overlay.color1,
        color2: base.overlay?.color2 ?? defaults.overlay.color2,
        color3: base.overlay?.color3 ?? defaults.overlay.color3,
        opacity1: base.overlay?.opacity1 ?? defaults.overlay.opacity1,
        opacity2: base.overlay?.opacity2 ?? defaults.overlay.opacity2,
        opacity3: base.overlay?.opacity3 ?? defaults.overlay.opacity3,
      },
      icons: (base.icons && Array.isArray(base.icons) && base.icons.length === 4)
        ? base.icons.map((i: any, idx: number) => ({
            enabled: i?.enabled ?? defaults.icons[idx].enabled,
            icon: i?.icon ?? defaults.icons[idx].icon,
            color: i?.color ?? defaults.icons[idx].color,
            size: Number(i?.size ?? defaults.icons[idx].size),
          }))
        : defaults.icons,
    };
  }, [tenant, previewConfig]);
  
  // Utiliser la logique de domaine
  const { getFilterParams } = useDomainQuery();
  const { domain, tenantId } = getFilterParams();
  
  const { items } = useElearningCartStore();
  const { itemCount, totalAmount, isEmpty } = useElearningCartStats();
  const { clearCart } = useElearningCartActions();
  
  const [isLoading, setIsLoading] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isCheckingAuth, setIsCheckingAuth] = useState(true);
  const [authChecked, setAuthChecked] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [currentUserProfile, setCurrentUserProfile] = useState<any>(null);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [currentOrder, setCurrentOrder] = useState<any>(null);
  
  // États pour le système de coupons
  const [appliedCouponCode, setAppliedCouponCode] = useState<string | null>(null);
  const [discountAmount, setDiscountAmount] = useState<number>(0);
  const [appliedCouponData, setAppliedCouponData] = useState<Coupon | null>(null);
  
  // Calcul du total avec réduction
  const finalAmount = totalAmount - discountAmount;
  
  // Gestionnaires pour les coupons
  const handleCouponApply = (discount: number, code: string, coupon: Coupon) => {
    setDiscountAmount(discount);
    setAppliedCouponCode(code);
    setAppliedCouponData(coupon);
  };

  const handleCouponRemove = () => {
    setDiscountAmount(0);
    setAppliedCouponCode(null);
    setAppliedCouponData(null);
  };
  
  // Formulaire client
  const [customerInfo, setCustomerInfo] = useState<ElearningCustomerInfo>({
    full_name: user?.user_metadata?.full_name || '',
    email: user?.email || '',
    phone: '',
    address: '',
    city: '',
    country: 'Cameroun',
    postal_code: ''
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  // Vérifier authentification existante au chargement
  useEffect(() => {
    // Éviter les exécutions multiples
    if (authChecked) return;
    
    const checkAuth = async () => {
      setIsCheckingAuth(true);
      
      try {
        // Rediriger si le panier est vide
        if (isEmpty) {
          navigate('/elearning/courses');
          return;
        }
        
        // Vérifier token étudiant d'abord
        const studentToken = localStorage.getItem('elearning_student_token');
        if (studentToken) {
          try {
            const profile = await getStudentProfileByToken(studentToken, domain || undefined, tenantId || undefined);
            if (profile) {
              // Étudiant connecté - pré-remplir toutes les informations depuis elearn_customer_profiles
              setCustomerInfo({
                full_name: profile.full_name || '',
                email: profile.email || '',
                phone: profile.phone || '',
                address: profile.address || '',
                city: profile.city || '',
                country: profile.country || 'Cameroun',
                postal_code: profile.postal_code || ''
              });
              setIsAuthenticated(true);
              setCurrentUserProfile({ type: 'student', profile });
              setAuthChecked(true);
              setIsCheckingAuth(false);
              return;
            }
          } catch (error) {
            // Token invalide - le supprimer
            localStorage.removeItem('elearning_student_token');
          }
        }
        
        // Vérifier auth Supabase (instructeur ou autre)
        if (user) {
          // Essayer de récupérer le profil client existant par email et domaine
          try {
            const existingProfile = await getStudentProfileByEmailAndDomain(user.email || '', domain || undefined, tenantId || undefined);
            if (existingProfile) {
              // Profil client trouvé - utiliser toutes les informations
              setCustomerInfo({
                full_name: existingProfile.full_name || user.user_metadata?.full_name || '',
                email: existingProfile.email || user.email || '',
                phone: existingProfile.phone || '',
                address: existingProfile.address || '',
                city: existingProfile.city || '',
                country: existingProfile.country || 'Cameroun',
                postal_code: existingProfile.postal_code || ''
              });
              setCurrentUserProfile({ type: 'supabase_with_profile', profile: existingProfile, user });
            } else {
              // Pas de profil client - utiliser les données Supabase
              setCustomerInfo({
                full_name: user.user_metadata?.full_name || '',
                email: user.email || '',
                phone: '',
                address: '',
                city: '',
                country: 'Cameroun',
                postal_code: ''
              });
              setCurrentUserProfile({ type: 'supabase_only', user });
            }
          } catch (error) {
            // En cas d'erreur, utiliser les données Supabase par défaut
            setCustomerInfo({
              full_name: user.user_metadata?.full_name || '',
              email: user.email || '',
              phone: '',
              address: '',
              city: '',
              country: 'Cameroun',
              postal_code: ''
            });
            setCurrentUserProfile({ type: 'supabase_only', user });
          }
          setIsAuthenticated(true);
          setAuthChecked(true);
          setIsCheckingAuth(false);
          return;
        }
        
        // Aucune authentification trouvée - continuer sans connexion obligatoire
        setIsAuthenticated(false);
        setCurrentUserProfile(null);
        
      } finally {
        setIsCheckingAuth(false);
        setAuthChecked(true);
      }
    };
    
    checkAuth();
  }, [isEmpty, navigate, user, toast, authChecked]);

  // Validation du formulaire
  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!customerInfo.full_name.trim()) {
      newErrors.full_name = 'Le nom complet est requis';
    }

    if (!customerInfo.email.trim()) {
      newErrors.email = 'L\'email est requis';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(customerInfo.email)) {
      newErrors.email = 'Format d\'email invalide';
    }

    if (!customerInfo.phone.trim()) {
      newErrors.phone = 'Le numéro de téléphone est requis';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Gestion de la soumission du formulaire
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      toast({
        title: 'Erreur de validation',
        description: 'Veuillez corriger les erreurs dans le formulaire',
        variant: 'destructive'
      });
      return;
    }

    if (!tenantId) {
      toast({
        title: 'Erreur',
        description: 'Impossible de traiter la commande. Veuillez réessayer.',
        variant: 'destructive'
      });
      return;
    }

    setIsProcessing(true);

    try {
      // Créer la commande sans marquer comme payée (status: created)
      const { order } = await createElearningOrder(
        tenantId,
        items,
        customerInfo,
        user?.id,
        false // Ne pas marquer comme payée
      );
      
      // Enregistrer l'utilisation du coupon si appliqué
      if (appliedCouponCode && discountAmount > 0) {
        try {
          await applyCoupon({
            coupon_code: appliedCouponCode,
            module_type: 'elearning',
            order_id: String(order.id),
            user_email: customerInfo.email,
            original_amount: totalAmount,
            discount_amount: discountAmount,
            final_amount: finalAmount
          });
        } catch (couponError) {
          console.error('Erreur lors de l\'enregistrement du coupon:', couponError);
          // Ne pas bloquer la commande si l'enregistrement du coupon échoue
        }
      }
      
      // Stocker la commande actuelle pour le modal de paiement
      setCurrentOrder({
        id: order.id,
        order_number: order.order_number,
        total_amount: finalAmount, // Utiliser le montant avec réduction
        customer_name: customerInfo.full_name,
        customer_email: customerInfo.email
      });

      // Afficher le modal de paiement
      setShowPaymentModal(true);
      setIsProcessing(false);

    } catch (error) {
      console.error('Erreur lors de la création de la commande:', error);
      toast({
        title: 'Erreur de commande',
        description: 'Une erreur est survenue lors du traitement de votre commande. Veuillez réessayer.',
        variant: 'destructive'
      });
      setIsProcessing(false);
    }
  };
  
  // Gestion du succès du paiement
  const handlePaymentSuccess = () => {
    // Vider le panier
    clearCart();
    
    // Fermer le modal de paiement
    setShowPaymentModal(false);
    
    // Rediriger vers la page de confirmation
    if (currentOrder) {
      navigate(`/elearning/confirmation/${currentOrder.id}`);
    }
  };

  // Gestion de la déconnexion
  const handleLogout = async () => {
    try {
      if (currentUserProfile?.type === 'student') {
        // Déconnexion étudiant
        const studentToken = localStorage.getItem('elearning_student_token');
        if (studentToken) {
          await invalidateStudentAccessToken(studentToken);
        }
      } else if (currentUserProfile?.type?.includes('supabase')) {
        // Déconnexion Supabase (instructeur)
        // Note: La déconnexion Supabase sera gérée par useAuth
      }
      
      // Réinitialiser les états
      setIsAuthenticated(false);
      setCurrentUserProfile(null);
      setCustomerInfo({
        full_name: '',
        email: '',
        phone: '',
        address: '',
        city: '',
        country: 'Cameroun',
        postal_code: ''
      });
      
      toast({
        title: 'Déconnexion réussie',
        description: 'Vous avez été déconnecté avec succès',
        variant: 'default'
      });
    } catch (error) {
      console.error('Erreur lors de la déconnexion:', error);
      toast({
        title: 'Erreur de déconnexion',
        description: 'Une erreur est survenue lors de la déconnexion',
        variant: 'destructive'
      });
    }
  };

  // Gestion des changements de formulaire
  const handleInputChange = (field: keyof ElearningCustomerInfo, value: string) => {
    setCustomerInfo(prev => ({ ...prev, [field]: value }));
    
    // Supprimer l'erreur si le champ est maintenant valide
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  // Afficher écran de chargement pendant vérification d'auth
  if (isCheckingAuth) {
    return (
      <PublicLayout>
        <div className="min-h-screen relative overflow-hidden">
          {/* Fond avec la nouvelle palette de couleurs SIS */}
          <div 
            className="fixed inset-0 z-0 bg-center bg-no-repeat bg-cover"
            style={{
              backgroundImage: tenant?.config?.bg ? `url(${tenant.config.bg})` : 'url()',
              backgroundAttachment: 'fixed'
            }}
          >
            {elearningTheme.overlay.enabled && (
              <div className="absolute inset-0 backdrop-blur-[2px]" style={{
                backgroundImage: elearningTheme.overlay.useGradient
                  ? `linear-gradient(${(
                      {
                        'to-b': 'to bottom',
                        'to-t': 'to top',
                        'to-r': 'to right',
                        'to-l': 'to left',
                        'to-tr': 'to top right',
                        'to-br': 'to bottom right',
                        'to-tl': 'to top left',
                        'to-bl': 'to bottom left',
                      } as Record<string, string>
                    )[elearningTheme.overlay.orientation] || 'to bottom'},
                      ${hexToRgba(elearningTheme.overlay.color1, elearningTheme.overlay.opacity1)},
                      ${hexToRgba(elearningTheme.overlay.color2, elearningTheme.overlay.opacity2)},
                      ${hexToRgba(elearningTheme.overlay.color3, elearningTheme.overlay.opacity3)}
                    )`
                  : undefined,
                backgroundColor: !elearningTheme.overlay.useGradient
                  ? hexToRgba(elearningTheme.overlay.color1, elearningTheme.overlay.opacity2)
                  : undefined,
              }} />
            )}
          </div>

          <div className="relative z-20 flex items-center justify-center min-h-screen py-12 px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="flex flex-col items-center justify-center space-y-6"
            >
              <div className="relative">
                <div className="w-16 h-16 border-4 rounded-full animate-spin"
                  style={{
                    borderColor: hexToRgba(tenant?.config?.color1 || '#fe8d17', 0.2),
                    borderTopColor: tenant?.config?.color1 || '#fe8d17'
                  }}
                ></div>
                <div className="absolute inset-0 w-16 h-16 border-4 border-transparent rounded-full animate-spin" 
                  style={{ 
                    borderTopColor: hexToRgba(tenant?.config?.color2 || '#008aef', 0.3),
                    animationDirection: 'reverse',
                    animationDuration: '1.5s'
                  }}
                ></div>
              </div>
              <div className="text-center">
                <h3 className="text-xl font-semibold mb-2" style={{ color: tenant?.config?.color3 || '#ffffff' }}>Vérification de votre connexion...</h3>
                <p style={{ color: hexToRgba(tenant?.config?.color3 || '#ffffff', 0.7) }}>Nous vérifions vos informations d'authentification</p>
              </div>
            </motion.div>
          </div>
        </div>
      </PublicLayout>
    );
  }

  if (isEmpty) {
    return null; // Le useEffect redirigera
  }

  return (
    <PublicLayout>
      <SEOMetaTags 
        title="Finaliser votre commande E-Learning"
        description="Procédez au paiement de vos cours sélectionnés."
        keywords="paiement, commande, e-learning, checkout"
      />

      {/* Fond avec la nouvelle palette de couleurs SIS */}
      <div 
        className="fixed inset-0 z-0 bg-center bg-no-repeat bg-cover"
        style={{
          backgroundImage: tenant?.config?.bg ? `url(${tenant.config.bg})` : 'url()',
          backgroundAttachment: 'fixed'
        }}
      >
        {elearningTheme.overlay.enabled && (
          <div className="absolute inset-0 backdrop-blur-[2px]" style={{
            backgroundImage: elearningTheme.overlay.useGradient
              ? `linear-gradient(${(
                  {
                    'to-b': 'to bottom',
                    'to-t': 'to top',
                    'to-r': 'to right',
                    'to-l': 'to left',
                    'to-tr': 'to top right',
                    'to-br': 'to bottom right',
                    'to-tl': 'to top left',
                    'to-bl': 'to bottom left',
                  } as Record<string, string>
                )[elearningTheme.overlay.orientation] || 'to bottom'},
                  ${hexToRgba(elearningTheme.overlay.color1, elearningTheme.overlay.opacity1)},
                  ${hexToRgba(elearningTheme.overlay.color2, elearningTheme.overlay.opacity2)},
                  ${hexToRgba(elearningTheme.overlay.color3, elearningTheme.overlay.opacity3)}
                )`
              : undefined,
            backgroundColor: !elearningTheme.overlay.useGradient
              ? hexToRgba(elearningTheme.overlay.color1, elearningTheme.overlay.opacity2)
              : undefined,
          }} />
        )}
      </div>

      {/* Particules flottantes avec couleurs personnalisables */}
      <div className="absolute inset-0 overflow-hidden z-10">
        <div className="absolute top-1/4 left-1/4 w-64 h-64 rounded-full blur-3xl animate-pulse" style={{ backgroundColor: hexToRgba(tenant?.config?.color1 || '#fe8d17', 0.08) }} />
        <div className="absolute top-3/4 right-1/4 w-96 h-96 rounded-full blur-3xl animate-pulse" style={{ backgroundColor: hexToRgba(tenant?.config?.color2 || '#008aef', 0.08), animationDelay: '2s' }} />
        <div className="absolute top-1/2 left-3/4 w-48 h-48 rounded-full blur-2xl animate-pulse" style={{ backgroundColor: hexToRgba(tenant?.config?.color1 || '#fe8d17', 0.15), animationDelay: '4s' }} />
      </div>
      
      {/* Particules flottantes avec couleurs et icônes personnalisables */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {/* Top-Left */}
        {elearningTheme.icons[0]?.enabled && (
          <motion.div
            className="absolute top-20 left-10"
            style={{ color: hexToRgba(elearningTheme.icons[0].color, 0.2) }}
            animate={{ y: [-20, 20, -20], rotate: [0, 180, 360] }}
            transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
          >
            {React.createElement(iconMap[elearningTheme.icons[0].icon] || BookOpen, { size: elearningTheme.icons[0].size })}
          </motion.div>
        )}
        {/* Top-Right */}
        {elearningTheme.icons[1]?.enabled && (
          <motion.div
            className="absolute top-40 right-20"
            style={{ color: hexToRgba(elearningTheme.icons[1].color, 0.2) }}
            animate={{ y: [20, -20, 20], rotate: [360, 180, 0] }}
            transition={{ duration: 10, repeat: Infinity, ease: "easeInOut" }}
          >
            {React.createElement(iconMap[elearningTheme.icons[1].icon] || Sparkles, { size: elearningTheme.icons[1].size })}
          </motion.div>
        )}
        {/* Bottom-Left */}
        {elearningTheme.icons[2]?.enabled && (
          <motion.div
            className="absolute bottom-40 left-20"
            style={{ color: hexToRgba(elearningTheme.icons[2].color, 0.2) }}
            animate={{ y: [-15, 15, -15], rotate: [0, 90, 180] }}
            transition={{ duration: 12, repeat: Infinity, ease: "easeInOut" }}
          >
            {React.createElement(iconMap[elearningTheme.icons[2].icon] || Diamond, { size: elearningTheme.icons[2].size })}
          </motion.div>
        )}
        {/* Bottom-Right */}
        {elearningTheme.icons[3]?.enabled && (
          <motion.div
            className="absolute bottom-20 right-10"
            style={{ color: hexToRgba(elearningTheme.icons[3].color, 0.2) }}
            animate={{ y: [15, -15, 15], rotate: [180, 270, 360] }}
            transition={{ duration: 9, repeat: Infinity, ease: "easeInOut" }}
          >
            {React.createElement(iconMap[elearningTheme.icons[3].icon] || Star, { size: elearningTheme.icons[3].size })}
          </motion.div>
        )}
      </div>

      <div className="relative container mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-12 md:pt-24 md:pb-20 z-20">
        {/* Header */}
        <div className="mb-8 py-16 text-center">
          <h1 className="text-4xl font-bold mb-4 drop-shadow-lg" style={{ color: tenant?.config?.color3 || '#ffffff' }}>Finaliser la commande</h1>
          <p className="text-lg drop-shadow-md" style={{ color: hexToRgba(tenant?.config?.color3 || '#ffffff', 0.9) }}>
              Vérifiez votre commande avant de procéder au paiement.
          </p>
        </div>

        {/* Contenu principal */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <motion.div 
            className="grid grid-cols-1 lg:grid-cols-2 gap-8"
            variants={fadeIn}
            initial="hidden"
            animate="visible"
          >
            {/* Formulaire de commande */}
            <div className="space-y-6">
              <Card className="bg-white/5 border backdrop-blur-sm shadow-xl text-white"
                style={{
                  borderColor: `${tenant?.config?.color1 || '#fe8d17'}33`
                }}
              >
                <CardHeader>
                  <CardTitle className="flex items-center justify-between" style={{ color: tenant?.config?.color3 || '#ffffff' }}>
                    <div className="flex items-center">
                      <CreditCard className="mr-2" style={{ color: tenant?.config?.color1 || '#fe8d17' }} size={24} />
                      Informations de facturation
                    </div>
                    {isAuthenticated && currentUserProfile && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={handleLogout}
                        className="text-xs px-2 py-1 h-auto"
                        style={{
                          color: hexToRgba(tenant?.config?.color3 || '#ffffff', 0.7),
                          borderColor: hexToRgba(tenant?.config?.color3 || '#ffffff', 0.2)
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.color = tenant?.config?.color3 || '#ffffff';
                          e.currentTarget.style.backgroundColor = hexToRgba(tenant?.config?.color1 || '#fe8d17', 0.1);
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.color = hexToRgba(tenant?.config?.color3 || '#ffffff', 0.7);
                          e.currentTarget.style.backgroundColor = 'transparent';
                        }}
                      >
                        <LogOut size={12} className="mr-1" />
                        Se déconnecter ou changer de compte
                      </Button>
                    )}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleSubmit} className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="full_name" style={{ color: tenant?.config?.color3 || '#ffffff' }}>Nom complet *</Label>
                        <Input
                          id="full_name"
                          type="text"
                          value={customerInfo.full_name}
                          onChange={(e) => handleInputChange('full_name', e.target.value)}
                          className="bg-white/10 border-white/20 placeholder:text-white/50 focus:ring-2 focus:border-transparent outline-none transition-all duration-300"
                          style={{
                            color: tenant?.config?.color3 || '#ffffff',
                            '--tw-ring-color': tenant?.config?.color1 || '#fe8d17'
                          } as React.CSSProperties}
                          placeholder="Votre nom complet"
                        />
                        {errors.full_name && (
                          <p className="text-red-400 text-sm mt-1">{errors.full_name}</p>
                        )}
                      </div>

                      <div>
                        <Label htmlFor="email" style={{ color: tenant?.config?.color3 || '#ffffff' }}>Email *</Label>
                        <Input
                          id="email"
                          type="email"
                          value={customerInfo.email}
                          onChange={(e) => handleInputChange('email', e.target.value)}
                          className="bg-white/10 border-white/20 placeholder:text-white/50 focus:ring-2 focus:border-transparent outline-none transition-all duration-300"
                          style={{
                            color: tenant?.config?.color3 || '#ffffff',
                            '--tw-ring-color': tenant?.config?.color1 || '#fe8d17'
                          } as React.CSSProperties}
                          placeholder="votre@email.com"
                        />
                        {errors.email && (
                          <p className="text-red-400 text-sm mt-1">{errors.email}</p>
                        )}
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="phone" style={{ color: tenant?.config?.color3 || '#ffffff' }}>Téléphone *</Label>
                        <Input
                          id="phone"
                          type="tel"
                          value={customerInfo.phone}
                          onChange={(e) => handleInputChange('phone', e.target.value)}
                          className="bg-white/10 border-white/20 placeholder:text-white/50 focus:ring-2 focus:border-transparent outline-none transition-all duration-300"
                          style={{
                            color: tenant?.config?.color3 || '#ffffff',
                            '--tw-ring-color': tenant?.config?.color1 || '#fe8d17'
                          } as React.CSSProperties}
                          placeholder="+237 6XX XXX XXX"
                        />
                        {errors.phone && (
                          <p className="text-red-400 text-sm mt-1">{errors.phone}</p>
                        )}
                      </div>

                      <div>
                        <Label htmlFor="country" style={{ color: tenant?.config?.color3 || '#ffffff' }}>Pays</Label>
                        <Input
                          id="country"
                          type="text"
                          value={customerInfo.country}
                          onChange={(e) => handleInputChange('country', e.target.value)}
                          className="bg-white/10 border-white/20 placeholder:text-white/50 focus:ring-2 focus:border-transparent outline-none transition-all duration-300"
                          style={{
                            color: tenant?.config?.color3 || '#ffffff',
                            '--tw-ring-color': tenant?.config?.color1 || '#fe8d17'
                          } as React.CSSProperties}
                          placeholder="Cameroun"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="city" style={{ color: tenant?.config?.color3 || '#ffffff' }}>Ville</Label>
                        <Input
                          id="city"
                          type="text"
                          value={customerInfo.city}
                          onChange={(e) => handleInputChange('city', e.target.value)}
                          className="bg-white/10 border-white/20 placeholder:text-white/50 focus:ring-2 focus:border-transparent outline-none transition-all duration-300"
                          style={{
                            color: tenant?.config?.color3 || '#ffffff',
                            '--tw-ring-color': tenant?.config?.color1 || '#fe8d17'
                          } as React.CSSProperties}
                          placeholder="Yaoundé"
                        />
                      </div>

                      <div>
                        <Label htmlFor="postal_code" style={{ color: tenant?.config?.color3 || '#ffffff' }}>Code postal</Label>
                        <Input
                          id="postal_code"
                          type="text"
                          value={customerInfo.postal_code}
                          onChange={(e) => handleInputChange('postal_code', e.target.value)}
                          className="bg-white/10 border-white/20 placeholder:text-white/50 focus:ring-2 focus:border-transparent outline-none transition-all duration-300"
                          style={{
                            color: tenant?.config?.color3 || '#ffffff',
                            '--tw-ring-color': tenant?.config?.color1 || '#fe8d17'
                          } as React.CSSProperties}
                          placeholder="BP 1234"
                        />
                      </div>
                    </div>

                    <div>
                      <Label htmlFor="address" style={{ color: tenant?.config?.color3 || '#ffffff' }}>Adresse</Label>
                      <Input
                        id="address"
                        type="text"
                        value={customerInfo.address}
                        onChange={(e) => handleInputChange('address', e.target.value)}
                        className="bg-white/10 border-white/20 placeholder:text-white/50 focus:ring-2 focus:border-transparent outline-none transition-all duration-300"
                        style={{
                          color: tenant?.config?.color3 || '#ffffff',
                          '--tw-ring-color': tenant?.config?.color1 || '#fe8d17'
                        } as React.CSSProperties}
                        placeholder="Votre adresse complète"
                      />
                    </div>
                  </form>
                </CardContent>
              </Card>

              {/* Section de suggestion de connexion pour les non-connectés */}
              {!isAuthenticated && (
                <Card className="bg-white/5 border backdrop-blur-sm shadow-xl text-white"
                  style={{
                    borderColor: `${tenant?.config?.color2 || '#008aef'}33`
                  }}
                >
                  <CardHeader>
                    <CardTitle className="flex items-center" style={{ color: tenant?.config?.color3 || '#ffffff' }}>
                      <UserPlus className="mr-2" style={{ color: tenant?.config?.color2 || '#008aef' }} size={24} />
                      Connexion recommandée (optionnelle)
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      <p className="text-sm" style={{ color: hexToRgba(tenant?.config?.color3 || '#ffffff', 0.8) }}>
                        Vous pouvez finaliser votre achat sans vous connecter, mais la connexion vous permet de :
                      </p>
                      <ul className="text-sm space-y-2" style={{ color: hexToRgba(tenant?.config?.color3 || '#ffffff', 0.7) }}>
                        <li className="flex items-center">
                          <CheckCircle size={14} className="mr-2" style={{ color: tenant?.config?.color2 || '#008aef' }} />
                          Pré-remplir automatiquement vos informations
                        </li>
                        <li className="flex items-center">
                          <CheckCircle size={14} className="mr-2" style={{ color: tenant?.config?.color2 || '#008aef' }} />
                          Accéder immédiatement à vos cours après l'achat
                        </li>
                        <li className="flex items-center">
                          <CheckCircle size={14} className="mr-2" style={{ color: tenant?.config?.color2 || '#008aef' }} />
                          Suivre votre progression et obtenir des certificats
                        </li>
                        <li className="flex items-center">
                          <CheckCircle size={14} className="mr-2" style={{ color: tenant?.config?.color2 || '#008aef' }} />
                          Recevoir le support de l'instructeur
                        </li>
                      </ul>
                      <div className="pt-2">
                        <Button 
                          onClick={() => navigate('/auth/login?redirect=/elearning/checkout')}
                          size="sm"
                          className="shadow-lg hover:shadow-xl transition-all duration-300"
                          style={{
                            backgroundColor: tenant?.config?.color2 || '#008aef',
                            color: tenant?.config?.color3 || '#ffffff',
                            backgroundImage: `linear-gradient(to right, ${tenant?.config?.color2 || '#008aef'}, ${hexToRgba(tenant?.config?.color2 || '#008aef', 0.8)})`
                          }}
                        >
                          <UserPlus size={16} className="mr-2" />
                          Se connecter
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Méthode de paiement réelle */}
              <Card className="bg-white/5 border backdrop-blur-sm shadow-xl text-white"
                style={{
                  borderColor: `${tenant?.config?.color1 || '#fe8d17'}33`
                }}
              >
                <CardHeader>
                  <CardTitle style={{ color: tenant?.config?.color3 || '#ffffff' }}>Méthode de paiement</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="border rounded-lg p-4"
                    style={{
                      backgroundColor: hexToRgba(tenant?.config?.color1 || '#fe8d17', 0.1),
                      borderColor: hexToRgba(tenant?.config?.color1 || '#fe8d17', 0.3)
                    }}
                  >
                    <div className="flex items-center mb-2">
                      <CheckCircle className="mr-2" style={{ color: tenant?.config?.color1 || '#fe8d17' }} size={20} />
                      <h4 className="font-semibold" style={{ color: tenant?.config?.color3 || '#ffffff' }}>Paiement sécurisé</h4>
                    </div>
                    <p className="text-sm" style={{ color: hexToRgba(tenant?.config?.color3 || '#ffffff', 0.8) }}>
                      Après avoir validé votre commande, vous pourrez choisir votre méthode de paiement préférée.
                      Nous acceptons les paiements par Mobile Money (MTN, Orange), carte bancaire et PayPal.
                    </p>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Résumé de la commande */}
            <div className="space-y-6">
              <Card className="bg-white/5 border backdrop-blur-sm shadow-xl text-white"
                style={{
                  borderColor: `${tenant?.config?.color1 || '#fe8d17'}33`
                }}
              >
                <CardHeader>
                  <CardTitle style={{ color: tenant?.config?.color3 || '#ffffff' }}>Résumé de la commande</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Liste des cours */}
                  <div className="space-y-3">
                    {items.map((item) => (
                      <div key={item.course_id} className="flex items-start space-x-3 p-3 rounded-lg border"
                        style={{
                          backgroundColor: hexToRgba(tenant?.config?.color1 || '#fe8d17', 0.05),
                          borderColor: hexToRgba(tenant?.config?.color1 || '#fe8d17', 0.2)
                        }}
                      >
                        <div className="w-16 h-12 rounded overflow-hidden flex-shrink-0 border"
                          style={{
                            backgroundImage: `linear-gradient(to bottom right, ${hexToRgba(tenant?.config?.color2 || '#008aef', 0.2)}, ${hexToRgba(tenant?.config?.color1 || '#fe8d17', 0.2)})`,
                            borderColor: hexToRgba(tenant?.config?.color1 || '#fe8d17', 0.3)
                          }}
                        >
                          {item.thumbnail_url ? (
                            <img
                              src={item.thumbnail_url}
                              alt={item.title}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center">
                              <BookOpen size={16} style={{ color: hexToRgba(tenant?.config?.color3 || '#ffffff', 0.5) }} />
                            </div>
                          )}
                        </div>
                        <div className="flex-grow min-w-0">
                          <h4 className="font-medium text-sm line-clamp-2" style={{ color: tenant?.config?.color3 || '#ffffff' }}>{item.title}</h4>
                          <div className="flex items-center space-x-3 text-xs mt-1" style={{ color: hexToRgba(tenant?.config?.color3 || '#ffffff', 0.6) }}>
                            {item.instructor_name && (
                              <span className="flex items-center">
                                <Users size={10} className="mr-1" />
                                {item.instructor_name}
                              </span>
                            )}
                            {item.duration_hours && (
                              <span className="flex items-center">
                                <Clock size={10} className="mr-1" />
                                {formatCourseDuration(item.duration_hours)}
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="text-right flex-shrink-0">
                          <div className="font-semibold" style={{ color: tenant?.config?.color1 || '#fe8d17' }}>
                            {formatPrice(item.price, item.currency)}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  <Separator style={{ backgroundColor: hexToRgba(tenant?.config?.color1 || '#fe8d17', 0.2) }} />

                  {/* Totaux */}
                  <div className="space-y-2">
                    <div className="flex justify-between" style={{ color: hexToRgba(tenant?.config?.color3 || '#ffffff', 0.8) }}>
                      <span>Sous-total ({itemCount} cours)</span>
                      <span>{formatPrice(totalAmount)}</span>
                    </div>
                    {discountAmount > 0 && (
                      <div className="flex justify-between text-green-400">
                        <span>Réduction ({appliedCouponCode})</span>
                        <span>-{formatPrice(discountAmount)}</span>
                      </div>
                    )}
                    <div className="flex justify-between" style={{ color: hexToRgba(tenant?.config?.color3 || '#ffffff', 0.8) }}>
                      <span>TVA</span>
                      <span>Incluse</span>
                    </div>
                    <Separator style={{ backgroundColor: hexToRgba(tenant?.config?.color1 || '#fe8d17', 0.2) }} />
                    <div className="flex justify-between font-bold text-lg">
                      <span style={{ color: tenant?.config?.color3 || '#ffffff' }}>Total</span>
                      <span style={{ color: tenant?.config?.color1 || '#fe8d17' }}>{formatPrice(finalAmount)}</span>
                    </div>
                  </div>
                  
                  {/* Section Code Promo */}
                  <div className="border-t pt-4" style={{ borderColor: hexToRgba(tenant?.config?.color1 || '#fe8d17', 0.2) }}>
                    <CouponInput
                      module="elearning"
                      amount={totalAmount}
                      userEmail={customerInfo.email}
                      onApply={handleCouponApply}
                      onRemove={handleCouponRemove}
                      appliedCoupon={appliedCouponCode}
                      disabled={isProcessing || !customerInfo.email}
                    />
                  </div>

                  {/* Avantages */}
                  <div className="border rounded-lg p-4"
                    style={{
                      backgroundColor: hexToRgba(tenant?.config?.color1 || '#fe8d17', 0.1),
                      borderColor: hexToRgba(tenant?.config?.color1 || '#fe8d17', 0.3)
                    }}
                  >
                    <h4 className="font-semibold mb-2 flex items-center" style={{ color: tenant?.config?.color3 || '#ffffff' }}>
                      <CheckCircle className="mr-2" style={{ color: tenant?.config?.color1 || '#fe8d17' }} size={16} />
                      Inclus dans votre achat :
                    </h4>
                    <ul className="text-sm space-y-1" style={{ color: hexToRgba(tenant?.config?.color3 || '#ffffff', 0.8) }}>
                      <li>• Accès à vie aux cours</li>
                      <li>• Certificat de completion</li>
                      <li>• Support de l'instructeur</li>
                      <li>• Accès mobile et desktop</li>
                      <li>• Mises à jour gratuites</li>
                    </ul>
                  </div>
                </CardContent>
                <CardFooter className="flex flex-col space-y-3">
                  <Button 
                    onClick={handleSubmit}
                    disabled={isProcessing}
                    size="lg" 
                    className="w-full shadow-lg hover:shadow-xl transition-all duration-300 font-bold"
                    style={{
                      backgroundColor: tenant?.config?.color1 || '#fe8d17',
                      color: tenant?.config?.color3 || '#ffffff',
                      backgroundImage: `linear-gradient(to right, ${tenant?.config?.color1 || '#fe8d17'}, ${tenant?.config?.color1 || '#e47c15'})`,
                      borderColor: hexToRgba(tenant?.config?.color1 || '#fe8d17', 0.4),
                      boxShadow: `0 4px 15px ${hexToRgba(tenant?.config?.color1 || '#fe8d17', 0.4)}`
                    }}
                    onMouseEnter={(e) => {
                      if (!isProcessing) {
                        e.currentTarget.style.backgroundImage = `linear-gradient(to right, ${tenant?.config?.color1 || '#e07a10'}, ${tenant?.config?.color1 || '#fe8d17'})`;
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!isProcessing) {
                        e.currentTarget.style.backgroundImage = `linear-gradient(to right, ${tenant?.config?.color1 || '#fe8d17'}, ${tenant?.config?.color1 || '#e47c15'})`;
                      }
                    }}
                  >
                    {isProcessing ? (
                      <>
                        <Loader2 className="animate-spin mr-2" size={20} />
                        Traitement en cours...
                      </>
                    ) : (
                      <>
                        <CreditCard size={20} className="mr-2" />
                        Finaliser l'achat - {formatPrice(finalAmount)}
                      </>
                    )}
                  </Button>
                  
                  <Button 
                    variant="ghost" 
                    onClick={() => navigate('/elearning/cart')}
                    className="w-full transition-colors"
                    style={{
                      color: hexToRgba(tenant?.config?.color3 || '#ffffff', 0.7)
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.color = tenant?.config?.color3 || '#ffffff';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.color = hexToRgba(tenant?.config?.color3 || '#ffffff', 0.7);
                    }}
                  >
                    <ArrowLeft size={16} className="mr-2" />
                    Retour au panier
                  </Button>
                </CardFooter>
              </Card>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Modal de paiement */}
      {showPaymentModal && currentOrder && (
        <ElearningPaymentModal
          isOpen={showPaymentModal}
          onClose={() => setShowPaymentModal(false)}
          order={currentOrder}
          onPaymentSuccess={handlePaymentSuccess}
        />
      )}
    </PublicLayout>
  );
};

export default ElearningCheckoutPage;
